import { useEffect } from 'react';
import { Banner, Spinner } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon, SparkleIcon, XIcon } from '@/components/layout/icons';
import { AssistantComposer } from '@/components/fleet/board/assistant-composer';
import { AssistantMessageList } from '@/components/fleet/board/assistant-message-list';
import { useAssistantConversation } from '@/components/fleet/board/use-assistant-conversation';

type Props = {
  readonly onClose: () => void;
};

/**
 * The operator's conversation with the fleet assistant, the one their Telegram chat writes to. A labelled `aside` over
 * the board in the ticket panel's shape; it polls only while it is open.
 */
export function AssistantPanel({ onClose }: Props) {
  const t = useTranslations('pages.admin.fleet-board.assistant');
  const tErrors = useTranslations('errors');
  const { messages, pending, readFailed, send } = useAssistantConversation(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose();
    };

    document.addEventListener('keydown', onKey);

    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <aside
      aria-label={t('title')}
      className="absolute inset-y-0 right-0 z-10 flex w-full max-w-[640px] flex-col border-l border-border bg-surface shadow-xl max-[900px]:max-w-none max-[900px]:border-l-0 max-[900px]:shadow-none"
    >
      <header className="flex flex-none items-center gap-2 border-b border-border px-4 py-3">
        <SparkleIcon aria-hidden className="h-5 w-5 text-text-secondary" />
        <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-text-primary">{t('title')}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('close')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-tertiary hover:bg-hover-bg hover:text-text-primary"
        >
          <XIcon aria-hidden className="h-4 w-4" />
        </button>
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {messages === null ? (
          readFailed ? (
            <div className="p-4">
              <Banner variant="danger" icon={<AlertCircleIcon />} title={tErrors('generic.unexpected')} />
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center">
              <Spinner />
            </div>
          )
        ) : messages.length === 0 && !pending ? (
          <p className="m-auto p-4 text-center text-sm text-text-tertiary">{t('empty')}</p>
        ) : (
          <AssistantMessageList messages={messages} pending={pending} scrollToLatest />
        )}
      </div>
      <footer className="flex-none border-t border-border p-4">
        <AssistantComposer onSend={send} />
      </footer>
    </aside>
  );
}
